/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { isFunction } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { useLink } from '@zapier/common-hooks';

import {
  Alert,
  AlertDescription,
  Colors,
  Margin,
  SkeletonBlock,
  Spacer,
} from '@zapier/design-system';

import {
  CurrencyRulesetItem,
  PaymentApp,
  PendingPaymentWarningBar,
  useCurrencyRuleset,
  usePaymentSource,
  usePendingPayment,
} from '@zapier/payment';

import { CurrencyProvider } from '@zapier/currency';
import { buildDowngradeWarningUrl } from '@zapier/downgrade';

import {
  useMaybeSendFullStoryEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import {
  withApolloInjectionProvider,
  useApolloInjection,
} from '@zapier/checkout/src/contexts/ApolloInjection';
import { useChangePlan, useCurrentAccountId } from '@zapier/checkout/src/hooks';
import {
  ApolloInjection,
  ChangeType,
  Interval,
  PlanV2,
} from '@zapier/checkout/src/types';

import { ChangePlanCompleteModal } from '../ChangePlanCompleteModal';
import { Document } from '../Document';

import { AsyncOverlay } from './AsyncOverlay';
import { BillingOptions } from './BillingOptions';
import { ChangePlanButtons } from './ChangePlanButtons';
import { ChangePlanCurrencyDropdown } from './ChangePlanCurrencyDropdown';
import { ChangePlanHeader } from './ChangePlanHeader';
import { ChangePlanIndicator } from './ChangePlanIndicator';
import { ChangePlanSection } from './ChangePlanSection';
import { ChangePlanSummary } from './ChangePlanSummary';

import {
  useCurrentAccount,
  useInvoicePreview,
  usePlans,
  usePromoPlans,
  useSelectedCurrency,
} from './hooks';

import {
  getChangeLabel,
  getChangeLabelForPlanName,
} from '@zapier/checkout/src/utils';

const AUTO_UPGRADE_FEATURE_NAME = 'AUTO_UPGRADE';

type Props = {
  /** `upgrade`, `downgrade` etc. */
  changeType: ChangeType;
  /** ISO code for selected currency */
  currency?: string;
  /** The interval selected. Should be used when controlled via `onSelectInterval` */
  interval?: Interval;
  /** Hides the header */
  isCompact?: boolean;
  /** Set this to true when the change plan page appears inside an inline upgrade nudge. */
  isEmbeddedInInlineUpgrade?: boolean;
  /** Called when cancel button is clicked */
  onCancel: () => void;
  /** Called when change plan action is successful */
  onFinishChangePlan?: () => void;
  /** Called when interval is clicked. Passing a function here makes this a controlled component */
  onSelectInterval?: (interval: Interval) => void;
  /** Called when currency is selected. Passing a function here makes this a controlled component. Use with currency prop. */
  onSelectCurrency?: (currency: string) => void;
  /** Flag to indicate whether to route to downgrade confirmation page before downgrading */
  showDowngradeConfirmation?: boolean;
  /** Flag to indicate whether to show the change plan success modal or not */
  showSuccessModal: boolean;
  /** The name of the plan changing to */
  toPlanName: string;
} & ApolloInjection;

const changePlanCss = (props: Props) =>
  css([
    css`
      background-color: ${Colors.white};
      border: 1px solid #dadfe2;
      border-radius: 5px;
      margin: 2em auto;
      max-width: 800px;
      padding: 2em;
      width: auto;
    `,
    props.isCompact &&
      css`
        border: none;
      `,
  ]);

const buttonsCss = css`
  margin-top: 2em;
`;

const errorCss = css`
  margin-top: 18px;
  text-align: center;
`;

const findPlans = (
  toPlanName: string,
  plans: PlanV2[],
  currentInterval: Interval
) => {
  const altInterval = currentInterval === 'year' ? 'month' : 'year';
  const toPlanNameLower = toPlanName.toLowerCase();

  let toPlan = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() && currentInterval === p.interval
  );

  toPlan =
    toPlan ??
    plans.find(
      p =>
        toPlanNameLower === p.name.toLowerCase() && altInterval === p.interval
    );

  // The alternative interval plan for the selected plan
  const toPlanAlt = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() &&
      p.tasks === toPlan?.tasks &&
      p.zaps === toPlan?.zaps &&
      p.interval !== toPlan.interval
  );

  return [toPlan, toPlanAlt];
};

const ChangePlanComponent = (props: Props) => {
  const {
    toPlanName,
    changeType,
    currency,
    interval,
    isEmbeddedInInlineUpgrade = false,
    onSelectCurrency,
    onSelectInterval,
    showSuccessModal,
  } = props;

  const { useQuery, useMutation } = useApolloInjection();
  const { routeTo } = useLink();
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId({ useQuery });

  const {
    currentAccount,
    isLoading: isLoadingCurrentAccount,
  } = useCurrentAccount(accountId!, { skip: !accountId }, useQuery);

  const autoUpgradeEnrollment = (
    currentAccount?.featureEnrollmentList || []
  ).find(e => e?.featureName === AUTO_UPGRADE_FEATURE_NAME);

  const { currentPlan, plans, isLoading: isLoadingPlans } = usePlans(
    accountId!,
    { skip: !accountId },
    useQuery
  );

  const { isLoading: isLoadingPromoPlans, promoPlans } = usePromoPlans(
    useQuery
  );

  // INTERVAL STATE -------------
  const [selectedInterval, setSelectedInterval] = useState<Interval>('year');
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  // CURRENCY STATE -------------
  const {
    selectedCurrency,
    setSelectedCurrency,
    isLoading: isLoadingSelectedCurrency,
  } = useSelectedCurrency({
    currency,
    onSelectCurrency,
    useQuery,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
    useQuery,
  });

  const { pendingInvoice } = usePendingPayment({ useQuery });

  const {
    currencyRuleset,
    isLoading: isLoadingCurrencyRuleset,
  } = useCurrencyRuleset({ useQuery });

  const isLoading =
    isLoadingCurrentAccount ||
    isLoadingPlans ||
    isLoadingPromoPlans ||
    isLoadingSelectedCurrency ||
    isLoadingCurrencyRuleset;

  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === 'upgrade',
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  // INVOICE PREVIEW -------------
  const {
    error: invoiceError,
    invoice,
    isLoading: isLoadingInvoice,
  } = useInvoicePreview({
    accountId: currentAccount?.id as number,
    planId: toPlan?.id,
    currency: selectedCurrency.name,
    useQuery,
  });

  // CHANGE PLAN -------------
  const {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
    pausedZaps,
  } = useChangePlan({
    accountId,
    planId: toPlan?.id,
    downgradeReason: null,
    currency: selectedCurrency.name,
    invoicedAt: invoice?.invoicedAt,
    useQuery,
    useMutation,
  });

  const onChangePlan = async () => {
    if (
      changeType === 'downgrade' &&
      props.showDowngradeConfirmation &&
      routeTo
    ) {
      return routeTo(
        buildDowngradeWarningUrl(
          currentPlan?.name ?? '',
          toPlanName,
          currentInterval,
          selectedCurrency.name
        )
      );
    }

    await changePlan();

    trackEvent('Billing: plan changed', {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent('Billing: cancelChangePlan', {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: 'first step',
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const changeLabel = getChangeLabel(changeType);
  const heading = getChangeLabelForPlanName(changeType, toPlan?.name ?? '');
  const subheading = `${changeLabel} account`;

  const submitText =
    changeType === 'change'
      ? `${changeLabel} Account Billing`
      : `${changeLabel} My Account`;

  const hasNoPaymentSource =
    toPlan && toPlan.amount !== 0 && (!primarySource || forceSourceEdit);

  const rulesetFilteredByCurrency = currencyRuleset.filter(
    (rule: CurrencyRulesetItem) => rule.currency === selectedCurrency.name
  );

  const supportedPaymentMethods = rulesetFilteredByCurrency.map(
    (rule: CurrencyRulesetItem) => rule.method
  );
  const supportedPaymentSubmethods = rulesetFilteredByCurrency.map(
    (rule: CurrencyRulesetItem) => rule.submethod
  );

  const hasUnsupportedPaymentSource =
    (primarySource?.method &&
      !supportedPaymentMethods.includes(primarySource.method)) ||
    (primarySource?.submethod &&
      !supportedPaymentSubmethods.includes(primarySource.submethod));

  /**
   *  A user on the same plan and interval shouldn't be allowed to proceed since it's essentially a no-op
   */
  const isSamePlanAndInterval =
    currentPlan?.id === toPlan?.id &&
    currentPlan?.interval === toPlan?.interval;

  const isSubmitDisabled =
    isLoadingInvoice ||
    hasNoPaymentSource ||
    Boolean(pendingInvoice) ||
    hasUnsupportedPaymentSource ||
    isSamePlanAndInterval;

  const popoverText = hasNoPaymentSource
    ? 'Please add a payment method.'
    : pendingInvoice
    ? 'Cannot change plan with a pending payment.'
    : hasUnsupportedPaymentSource
    ? 'Please use a supported payment method.'
    : null;

  return (
    <CurrencyProvider selectedCurrency={selectedCurrency}>
      <Document title={heading}>
        {isLoading ? (
          /**
           * 600x750 makes loading the change plan page in a modal look smooth. If we want a different
           * loading block for non-modal usage, we may want to add an `isInModal` prop or something
           * to the `ChangePlan` component and conditionally render an appropriate loading block.
           */
          <SkeletonBlock height={600} width={750} />
        ) : (
          <Fragment>
            {showSuccessModal && hasChangedPlan && (
              <ChangePlanCompleteModal
                changeType={changeType}
                hasPausedZaps={pausedZaps.length > 0}
                newPlan={toPlan as PlanV2}
              />
            )}
            <AsyncOverlay
              isVisible={isChangingPlan}
              renderAsyncIndicator={() => <ChangePlanIndicator />}
            >
              <form
                css={changePlanCss(props)}
                onSubmit={e => e.preventDefault()}
              >
                {!props.isCompact && (
                  <ChangePlanHeader
                    accountName={
                      currentAccount?.owner?.email ?? currentAccount?.name
                    }
                    companyName={currentAccount?.companyName}
                    heading={heading}
                    isEmbeddedInInlineUpgrade={isEmbeddedInInlineUpgrade}
                    subheading={subheading}
                  />
                )}

                <ChangePlanCurrencyDropdown
                  onSelectCurrency={setSelectedCurrency}
                  selectedCurrency={selectedCurrency}
                />
                <Spacer height={20} />

                {pendingInvoice && (
                  <Margin margin="0 0 10px 0">
                    <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
                  </Margin>
                )}

                <BillingOptions
                  isAutoUpgradeEnrolled={autoUpgradeEnrollment?.isEnrolled}
                  onSelectOption={onSelectIntervalCallback}
                  planOptions={([toPlan, toPlanAlt] as PlanV2[])
                    .sort((a, b) => b.amount - a.amount)
                    .filter(p => p !== undefined)}
                  selectedOption={currentInterval}
                />

                {toPlan && toPlan.amount !== 0 && (
                  <ChangePlanSection title="Payment Details">
                    <PaymentApp
                      editInModal={true}
                      selectedCurrency={selectedCurrency}
                      useMutation={useMutation}
                      useQuery={useQuery}
                    />
                  </ChangePlanSection>
                )}

                <ChangePlanSection title="Summary">
                  {toPlan && (
                    <ChangePlanSummary
                      error={invoiceError}
                      invoice={invoice}
                      isLoading={isLoadingInvoice}
                      plan={toPlan}
                    />
                  )}
                </ChangePlanSection>

                <div css={buttonsCss}>
                  <ChangePlanButtons
                    isDisabled={isSubmitDisabled}
                    onCancelChangePlan={onCancelChange}
                    onChangePlan={onChangePlan}
                    popoverText={popoverText}
                    submitText={submitText}
                  />
                </div>

                {changePlanError && (
                  <div css={errorCss}>
                    <Alert type="error">
                      <AlertDescription>{changePlanError}</AlertDescription>
                    </Alert>
                  </div>
                )}
              </form>
            </AsyncOverlay>
          </Fragment>
        )}
      </Document>
    </CurrencyProvider>
  );
};

export const ChangePlan = withApolloInjectionProvider<Props>(
  ChangePlanComponent
);
